import { FC, ReactNode } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { createUseStyles } from 'react-jss';
import { IonButton, IonIcon } from '@ionic/react';
import { close } from 'ionicons/icons';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { CareMapState } from '../../caremap/CareMapState';
import { ICollaboratorPermission } from '../../share-caremap/CommunityClient';
import { AppPrincipal, isAnonymousViewer } from '../../auth/AppPrincipal';
import { CommunityModelFactory } from '../../share-caremap/CommunityModelFactory';
import { SharingAndAccessState } from '../../share-caremap/sharing-and-access/SharingAndAccessState';
import { LockIcon, EyeIcon, ShareCareIcon } from '../../theme/Icons';
import { MemberAvatar } from '../../household/MemberAvatar';
import { useNudgeStorage } from '../../utils/useNudgeStorage';

const useStyles = createUseStyles({
    // Neutral state-of-sharing row (not an alert): shared-is-the-default, so a hidden note is a
    // deliberate choice, not something to warn about.
    stateRow: {
        marginTop: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: 'var(--ion-color-step-50)',
        borderRadius: 10,
        padding: '11px 13px'
    },
    stateLead: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        color: 'var(--ion-color-medium)'
    },
    stateText: {
        flex: 1,
        minWidth: 0,
        fontSize: 14,
        color: 'var(--ion-color-step-750)',
        lineHeight: 1.4
    },
    stateHiddenClause: {
        color: 'var(--ion-color-step-600)'
    },
    statePending: {
        color: 'var(--ion-color-step-400)'
    },
    stateAction: {
        flexShrink: 0,
        color: 'var(--ion-color-primary)',
        fontWeight: 500,
        fontSize: 14,
        whiteSpace: 'nowrap',
        cursor: 'pointer'
    },
    cluster: {
        display: 'flex'
    },
    avatarPhoto: {
        display: 'inline-flex',
        borderRadius: '50%',
        boxShadow: '0 0 0 2px #fff'
    },
    avatarOverflow: {
        boxSizing: 'border-box',
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
        background: 'var(--ion-color-step-100)',
        color: 'var(--ion-color-step-750)',
        border: '2px solid #fff'
    },
    banner: {
        marginTop: 14,
        background: 'rgba(17,120,157,0.10)',
        borderRadius: 10,
        padding: 14
    },
    bannerTopRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8
    },
    bannerHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: 'var(--ion-color-primary)',
        fontSize: 16,
        fontWeight: 600
    },
    bannerClose: {
        alignSelf: 'flex-start',
        margin: '-6px -6px 0 0',
        '--padding-start': '2px',
        '--padding-end': '2px',
        height: 28
    },
    bannerMsg: {
        fontSize: 14,
        color: 'var(--ion-color-step-750)',
        marginTop: 6,
        lineHeight: 1.5
    },
    inviteBtn: {
        marginTop: 12
    }
});

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

export interface IAvatarPerson {
    key: string;
    name: string;
    photo?: string;
    // Authorised route for `photo`, set only when it is an uploaded care map photo.
    photoHref?: string;
    avatar?: string;
    color?: string;
}

export interface INoteShareHookViewProps {
    isPrivate: boolean;
    // accepted collaborators, split by role, for the "who can see this" sentence
    coOwnerCount: number;
    viewerCount: number;
    pendingCount: number;
    // avatars for people who can actually see this note (co-owners + viewers when shared,
    // co-owners only when hidden)
    avatars: IAvatarPerson[];
    patientName: string;
    onManage: () => void;
    onInvite: () => void;
    // The empty-state invite banner (only) can be dismissed per care map; the status rows can't.
    inviteDismissed: boolean;
    onDismissInvite: () => void;
}

// Pure presentation — every state is derived from these props, so the states can be previewed
// and unit-reasoned without a live care map / community. The row's action is always "Manage"
// (people/access); a note's visibility is a note property, changed from the note menu.
export const NoteShareHookView: FC<INoteShareHookViewProps> = ({
    isPrivate, coOwnerCount, viewerCount, pendingCount, avatars, patientName, onManage, onInvite, inviteDismissed, onDismissInvite
}) => {
    const classes = useStyles();

    const shown = avatars.length > 4 ? avatars.slice(0, 3) : avatars;
    const overflow = avatars.length - shown.length;
    const pendingSuffix = pendingCount > 0 ? ` · ${pendingCount} pending` : '';

    const avatarCluster = (
        <div className={classes.cluster}>
            {shown.map((person, i) => (
                <div key={person.key} style={i === 0 ? undefined : { marginLeft: -8 }}>
                    <div className={`${classes.avatarPhoto} mp-mask`}>
                        <MemberAvatar
                            xsmall
                            member={{ photo: person.photo, avatar: person.avatar, color: person.color, links: person.photoHref ? { photo: { href: person.photoHref } } : undefined }}
                            name={person.name}
                            noImageFallback="initials"
                        />
                    </div>
                </div>
            ))}
            {overflow > 0 && (
                <div style={{ marginLeft: -8 }}><div className={classes.avatarOverflow}>+{overflow}</div></div>
            )}
        </div>
    );

    const row = (lead: ReactNode, text: ReactNode, rowDataType: string) => (
        <div data-type="note-share-hook">
            <div className={classes.stateRow} data-type={rowDataType}>
                <div className={classes.stateLead}>{lead}</div>
                <div className={classes.stateText}>{text}</div>
                <div className={classes.stateAction} data-type="note-share-manage" onClick={onManage}>Manage</div>
            </div>
        </div>
    );

    // Hidden note: co-owners (and you) still see it; only viewers are blocked.
    if (isPrivate) {
        const canSeePart = coOwnerCount > 0 ? `${plural(coOwnerCount, 'co-owner')} can see this` : 'Only you can see this';
        return row(
            avatars.length > 0 ? avatarCluster : <LockIcon color="medium" size="small" />,
            <>
                {canSeePart}
                {viewerCount > 0 && <span className={classes.stateHiddenClause}> · hidden from {plural(viewerCount, 'viewer')}</span>}
                {pendingCount > 0 && <span className={classes.statePending}>{pendingSuffix}</span>}
            </>,
            'note-share-hidden'
        );
    }

    // Shared note with an audience: everyone with access can see it.
    if (coOwnerCount + viewerCount > 0) {
        const parts: string[] = [];
        if (coOwnerCount > 0) {
            parts.push(plural(coOwnerCount, 'co-owner'));
        }
        if (viewerCount > 0) {
            parts.push(plural(viewerCount, 'viewer'));
        }
        return row(
            avatarCluster,
            <>
                {parts.join(' and ')} can see this
                {pendingCount > 0 && <span className={classes.statePending}>{pendingSuffix}</span>}
            </>,
            'note-shared-with'
        );
    }

    // Shared but only invitations are out — no one has accepted yet.
    if (pendingCount > 0) {
        return row(<EyeIcon color="medium" size="small" />, `${plural(pendingCount, 'invite')} pending`, 'note-shared-with');
    }

    // Shared with no one yet — the amplified activation hook. This is the only dismissable state:
    // it's a pure nudge, and once anyone is invited it's replaced by a status row above.
    if (inviteDismissed) {
        return null;
    }
    return (
        <div data-type="note-share-hook">
            <div className={classes.banner} data-type="note-share-invite">
                <div className={classes.bannerTopRow}>
                    <div className={`${classes.bannerHead} mp-mask`}>
                        <ShareCareIcon color="primary" />
                        Share {patientName}'s Care
                    </div>
                    <IonButton className={classes.bannerClose} fill="clear" color="medium" shape="round" size="small" aria-label="Dismiss" data-type="note-share-invite-dismiss" onClick={onDismissInvite}>
                        <IonIcon slot="icon-only" icon={close} />
                    </IonButton>
                </div>
                <div className={classes.bannerMsg}>
                    Invite family or care team to keep them in the loop!
                </div>
                <IonButton className={classes.inviteBtn} expand="block" data-type="note-share-invite-btn" onClick={onInvite}>
                    Invite Someone
                </IonButton>
            </div>
        </div>
    );
};

export const NoteShareHook: FC<{ note: ICaremapNoteResponse }> = ({ note }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const communityFactory = useInstance(CommunityModelFactory);
    useSubscription(() => communityFactory);

    const sharingState = useInstance(SharingAndAccessState);

    const careMap = careMapState.getActiveCaremap();

    // Only the empty-state invite banner is dismissable, and per care map (device-local). Called
    // before the early return below so the hook runs unconditionally (Rules of Hooks).
    const [inviteVisible, dismissInvite] = useNudgeStorage({ nudgeId: careMap ? `note-share-invite-${careMap.careMapId}` : undefined });

    const isViewer = careMap && !careMap.links?.self?.actions.includes('put');
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    // The share hook is an editor affordance for managing who can see the care recipient's
    // shared notes; viewers and anonymous visitors get nothing.
    if (!careMap || isViewer || isAnonymous) {
        return null;
    }

    const community = communityFactory.forActiveCaremap();
    const coOwners = community.getPermanentCoOwners();
    const viewers = community.getPermanentViewers();
    const pendingCount = community.getInvites('co-owner').length + community.getInvites('view-only').length;

    // Avatar resolution mirrors SharingPeopleList so names/photos stay consistent with the
    // Sharing & Access sheet.
    const toAvatar = (p: ICollaboratorPermission): IAvatarPerson => {
        const link = careMapState.findMemberLinkForInvite({ email: p.invite.acceptedBy.email, accepterUserId: p.invite.acceptedBy.userId });
        const collaborator = p.collaborator;
        const name = link ? careMapState.getMemberDisplayName(link.member) : (collaborator?.name || p.invite.intendedRecipient.name || '');
        const householdMember = link?.hiveKey === 'household' ? link.member : undefined;
        return {
            key: `perm-${p.invite.token}`,
            name,
            photo: householdMember?.photo || collaborator?.pictureUrl,
            // Only set when the photo is an uploaded care map photo. A collaborator's profile
            // picture has no audited route yet.
            photoHref: householdMember?.photo ? householdMember.links?.photo?.href : undefined,
            avatar: householdMember?.avatar || collaborator?.avatar,
            color: householdMember?.color || collaborator?.color
        };
    };

    // Avatars = people who can actually see THIS note: everyone when shared, co-owners only
    // when hidden (co-owners see hidden notes; viewers don't).
    const canSee = note.isPrivate ? coOwners : [...coOwners, ...viewers];

    return (
        <NoteShareHookView
            isPrivate={Boolean(note.isPrivate)}
            coOwnerCount={coOwners.length}
            viewerCount={viewers.length}
            pendingCount={pendingCount}
            avatars={canSee.map(toAvatar)}
            patientName={careMapState.getPatientFirstName()}
            onManage={() => sharingState.open('Note Share Hook', { audience: 'has-people' })}
            onInvite={() => sharingState.open('Note Share Hook', { startOnAddPerson: true, audience: 'none' })}
            inviteDismissed={!inviteVisible}
            onDismissInvite={dismissInvite}
        />
    );
};
